/**
 * `skip-sharepoint-document` and `reopen-sharepoint-document` — CLM-115.
 *
 * The Bulk ingest planner recomputes its "To skip" list from scratch on every
 * crawl, so a reviewer who judges fifty of sixty rows sees the same sixty next
 * time. These two endpoints record and withdraw that judgement.
 *
 * ## Why the document, not the version
 *
 * Both address a document by `(drive_id, item_id)` — the pair `plan.ts` keys
 * its idempotency map on, and the only identity stable across two crawls. The
 * eTag is the *version*: it changes whenever the document is re-signed, so a
 * version-addressed skip would be undone by an upload nobody reviewed, and
 * could not name the `no_etag` rows at all (their eTag is `null` by
 * construction — see `SkippedDocumentSchema.etag`).
 *
 * `skipped_etag` is carried on the skip request as a record of what the
 * reviewer actually looked at, not as part of the address. D-115-1: a changed
 * document stays skipped, and the planner marks it `changed_since_skip` so it
 * is visible in the Skipped section without returning to the queue.
 *
 * ## Why POST for both, and why they are not one endpoint with a flag
 *
 * `EndpointDescriptor.method` admits only `GET` and `POST`, and both of these
 * write. They stay separate because they are different acts with different
 * audit meaning — `Create` and `Update` on the row — and because a boolean
 * `skipped: false` request is the shape that eventually gets sent by accident.
 */
import { z } from 'zod';
import { ETagSchema, GraphItemIdSchema, IngestPlannerReasonSchema } from './plan-sharepoint-ingest.js';
/**
 * The skip table rejects blank-after-trimming values, while preserving every
 * nonblank identifier and eTag byte-for-byte. Keep that write constraint at the
 * request boundary so an invalid client value is a 400 rather than a Postgres
 * CHECK violation surfaced as a 500.
 */
const NonBlankGraphItemIdSchema = GraphItemIdSchema.refine((value) => value.trim().length > 0, 'Must contain a non-whitespace character');
const NonBlankETagSchema = ETagSchema.refine((value) => value.trim().length > 0, 'Must contain a non-whitespace character');
/**
 * Both halves of the document's identity, and nothing else that identifies it.
 *
 * No file name, no web URL, no folder path: the server has all three from its
 * own crawl, and a client-supplied name here would be a second, unverified
 * source for what the row *is*.
 */
export const SharepointDocumentRefSchema = z
    .object({
    drive_id: NonBlankGraphItemIdSchema,
    item_id: NonBlankGraphItemIdSchema,
})
    .strict();
export const SkipSharepointDocumentRequestSchema = z
    .object({
    drive_id: NonBlankGraphItemIdSchema,
    item_id: NonBlankGraphItemIdSchema,
    /**
     * The version the reviewer had in front of them. `null` where the plan
     * showed none, which is exactly the `no_etag` skip reason — one of the two
     * groups this feature exists to clear.
     */
    skipped_etag: NonBlankETagSchema.nullable(),
    /**
     * What the planner listed the document under, recorded for context: a skip
     * reason, or a Type pending reason (CLM-122, R-122-6). The column is text, so
     * the vocabulary grew without a migration (`0099`).
     */
    planner_reason: IngestPlannerReasonSchema,
})
    .strict();
/**
 * Deliberately thin. The caller already knows what it skipped, and the plan is
 * the thing that reports state — a fatter response here would be a second
 * source of truth for the Skipped section.
 */
export const SkipSharepointDocumentResponseSchema = z
    .object({
    drive_id: NonBlankGraphItemIdSchema,
    item_id: NonBlankGraphItemIdSchema,
    skipped_utc: z.string().datetime({ offset: true }),
})
    .strict();
export const ReopenSharepointDocumentRequestSchema = SharepointDocumentRefSchema;
export const ReopenSharepointDocumentResponseSchema = z
    .object({
    drive_id: NonBlankGraphItemIdSchema,
    item_id: NonBlankGraphItemIdSchema,
    reopened_utc: z.string().datetime({ offset: true }),
})
    .strict();
//# sourceMappingURL=skip-sharepoint-document.js.map